<template>
  <v-menu
    v-model="showAuthServiceMenu"
    class="mb-4"
    :disabled="authorityServices.length < 2"
    location="bottom"
    tabindex="0"
  >
    <template #activator="{ props: ActivatorProps }">
      <button
        v-bind="ActivatorProps"
        :aria-label="i18n.t('labels.id_service')"
        class="d-flex flex-row flex-shrink-1 justify-center align-center ml-1 cursor-pointer"
        :class="{ disabled: authorityServices.length < 2 }"
        data-test-id="authority-service-menu"
        :data-test-selected="authorityServiceKey"
        tabindex="0"
      >
        <v-img
          v-if="showLogo"
          :alt="`${authorityServiceKey} logo`"
          class="ma-0 mr-1"
          :class="{
            ras: authorityServiceKey === 'ras',
            did: authorityServiceKey === 'did',
            sign8: authorityServiceKey === 'sign8'
          }"
          :height="authorityServiceKey === 'sign8' ? 20 : 14"
          :src="`/svg/authority-services/${authorityServiceKey}-logo.svg`"
        />
        <div v-else class="d-flex justify-end align-end mr-1 mt-1 sms-code">
          {{ i18n.t('authorities.sms_code') }}
        </div>
        <VIcon v-if="authorityServices.length > 1" color="black" :size="12">
          far fa-chevron-down
        </VIcon>
      </button>
    </template>
    <v-card class="pa-4" data-test-id="id-service-card" rounded="8px">
      <v-list
        class="pa-0 d-flex flex-column ga-2"
        data-test-id="id-service-list"
      >
        <v-list-item
          v-for="service in authorityServices"
          :key="service.key"
          class="authority-service-item pa-0"
          rounded="8px"
          tabindex="0"
          @keydown.enter="setAuthorityService(service.key)"
          @keydown.space="setAuthorityService(service.key)"
        >
          <AuthorityServiceItem
            :active="service.key === authorityServiceKey"
            :authority-service="service.key"
            :data-test-id="`${service.key}`"
            :signature-mode="signatureMode"
          />
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script lang="ts" setup>
import AuthorityServiceItem from '@/components/items/AuthorityServiceItem.vue';
import { SignatureMode } from '@/types/enums/SignatureMode.ts';
import { AuthorityService } from '@/types/enums/AuthorityService.ts';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEmitter } from '@/composables/useEmitter.ts';

const props = defineProps({
  authorityServices: { type: Array, default: () => [] },
  authorityService: { type: Object, default: null },
  signatureMode: { type: String, required: true }
});

const i18n = useI18n();
const emitter = useEmitter();
const showAuthServiceMenu = ref(false);

const authorityServiceKey = computed(() => {
  return props.authorityService?.key || null;
});

const showLogo = computed(() => {
  if (props.signatureMode === SignatureMode.ADVANCED) {
    return (
      authorityServiceKey.value === AuthorityService.DEEP_ID ||
      authorityServiceKey.value === AuthorityService.SIGN8
    );
  } else {
    return (
      props.authorityService !== null && props.authorityService !== undefined
    );
  }
});

function setAuthorityService(key: string) {
  emitter.$emit('set-authority-service', key);
  showAuthServiceMenu.value = false;
}
</script>

<style lang="scss" scoped>
.authority-service-item {
  border: 1px solid transparent;

  overflow: hidden;
  border-radius: 8px !important;

  :deep(.v-list-item__overlay),
  :deep(.v-list-item__underlay) {
    border-radius: inherit;
  }

  &:focus {
    border-color: rgb(var(--v-theme-primary));
  }

  &.v-list-item--active {
    border-color: rgb(var(--v-theme-primary));
  }
}

.sign8 {
  margin-bottom: -2px !important;
  width: 64px;
  object-fit: contain;
}

.did {
  margin-bottom: -5px !important;
  width: 64px;
  object-fit: contain;
}
.ras {
  margin-bottom: -1px !important;
  width: 64px;
  object-fit: contain;
}

.sms-code {
  font-weight: 600;
  font-size: 13px;
  line-height: 16px;
  color: black;
}

.disabled {
  &:hover {
    cursor: default;
  }
}
</style>
